import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { format, differenceInDays } from 'date-fns';
import {
  ArrowLeft,
  Search,
  Plus,
  X,
  Edit,
  Trash2,
  Check,
  Clock,
  Building2,
  MapPin,
  MessageSquare,
  Phone,
  Mail,
  Linkedin,
  Calendar as CalendarIcon,
  Save,
  AlertTriangle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: '',
  current_employer: '',
  job_title: '',
  designation: '',
  city: '',
  have_met_before: false,
  reached_out: false,
  reached_out_info: { date: '', not_decided_date: false, means: '' },
  remarks: '',
  custom_tags: [],
};

/* ── helper: check if 90+ days since last reached out ── */
function getInactiveDays(entry) {
  if (!entry.reached_out) return null; // never reached out — not "inactive", just pending
  const dateStr = entry.reached_out_info?.date;
  if (!dateStr) return null; // no date recorded
  try {
    return differenceInDays(new Date(), new Date(dateStr + 'T12:00:00'));
  } catch {
    return null;
  }
}

/* ────────────────────────────────────────────
   EntryCard – displays one catch-up row
   ──────────────────────────────────────────── */
function EntryCard({ entry, onEdit, onDelete, showInactiveWarning }) {
  const inactiveDays = showInactiveWarning ? getInactiveDays(entry) : null;
  const isInactive = inactiveDays !== null && inactiveDays >= 90;

  return (
    <div
      className={`flex items-center justify-between px-4 py-3 border rounded-lg transition-colors ${
        isInactive
          ? 'border-red-300 bg-red-50/50 hover:bg-red-50'
          : 'border-slate-200 hover:bg-slate-50'
      }`}
      data-testid={`catchup-entry-${entry.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium truncate ${isInactive ? 'text-red-800' : 'text-[#0F172A]'}`}>{entry.name}</span>
          {entry.reached_out ? (
            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
              <Check className="w-3 h-3" /> Reached out
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
              <Clock className="w-3 h-3" /> Not yet reached out
            </span>
          )}
          {isInactive && (
            <span
              className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded-full"
              data-testid={`inactive-badge-${entry.id}`}
            >
              <AlertTriangle className="w-3 h-3" /> {inactiveDays}d since last outreach
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
          {entry.current_employer && (
            <span className="flex items-center gap-1 truncate">
              <Building2 className="w-3 h-3" /> {entry.current_employer}
            </span>
          )}
          {entry.city && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3" /> {entry.city}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onEdit(entry)}
          className="h-8 w-8 text-slate-400 hover:text-[#0F172A]"
          data-testid={`edit-catchup-${entry.id}`}
        >
          <Edit className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(entry.id)}
          className="h-8 w-8 text-slate-400 hover:text-red-500"
          data-testid={`delete-catchup-${entry.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   EntryForm – FULLY SELF-CONTAINED
   Manages its own state so parent never
   re-renders during typing.
   ──────────────────────────────────────────── */
function EntryForm({ initialData, isEditing, formKey, onSave, onCancel }) {
  const [formData, setFormData] = useState(() => ({ ...initialData }));
  const [tagInput, setTagInput] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateReachedOutInfo = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      reached_out_info: { ...prev.reached_out_info, [field]: value },
    }));
  };

  const handleAddTag = () => {
    const t = tagInput.trim();
    if (t && !formData.custom_tags.includes(t)) {
      setFormData((prev) => ({ ...prev, custom_tags: [...prev.custom_tags, t] }));
    }
    setTagInput('');
  };

  const handleRemoveTag = (idx) => {
    setFormData((prev) => ({
      ...prev,
      custom_tags: prev.custom_tags.filter((_, i) => i !== idx),
    }));
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    onSave(formData);
  };

  return (
    <Card className="border-[#D4AF37]/30">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-heading text-[#0F172A]">
          {isEditing ? 'Edit Entry' : 'New Entry'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={formData.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="e.g., John Smith"
              data-testid="catchup-name-input"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Current Employer</Label>
            <Input
              value={formData.current_employer}
              onChange={(e) => updateField('current_employer', e.target.value)}
              placeholder="e.g., Goldman Sachs"
              data-testid="catchup-employer-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-700 font-medium">City</Label>
          <Input
            value={formData.city}
            onChange={(e) => updateField('city', e.target.value)}
            placeholder="e.g., Singapore"
            data-testid="catchup-city-input"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Job Title</Label>
            <Input
              value={formData.job_title}
              onChange={(e) => updateField('job_title', e.target.value)}
              placeholder="e.g., Managing Director"
              data-testid="catchup-job-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Designation</Label>
            <Input
              value={formData.designation}
              onChange={(e) => updateField('designation', e.target.value)}
              placeholder="e.g., Senior Partner"
              data-testid="catchup-designation-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-slate-700 font-medium">Have Met Before?</Label>
          <RadioGroup
            value={formData.have_met_before ? 'yes' : 'no'}
            onValueChange={(v) => updateField('have_met_before', v === 'yes')}
            className="flex gap-6"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id={`met-no-${formKey}`} />
              <Label htmlFor={`met-no-${formKey}`} className="cursor-pointer">No</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id={`met-yes-${formKey}`} />
              <Label htmlFor={`met-yes-${formKey}`} className="cursor-pointer">Yes</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-3">
          <Label className="text-slate-700 font-medium">Reached Out Yet?</Label>
          <RadioGroup
            value={formData.reached_out ? 'yes' : 'no'}
            onValueChange={(v) => updateField('reached_out', v === 'yes')}
            className="flex gap-6"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id={`reached-no-${formKey}`} data-testid="reached-out-no" />
              <Label htmlFor={`reached-no-${formKey}`} className="cursor-pointer">No</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id={`reached-yes-${formKey}`} data-testid="reached-out-yes" />
              <Label htmlFor={`reached-yes-${formKey}`} className="cursor-pointer">Yes</Label>
            </div>
          </RadioGroup>

          {formData.reached_out && (
            <div className="ml-4 pl-4 border-l-2 border-[#D4AF37]/30 space-y-4">
              <div className="space-y-2">
                <Label className="text-slate-600">Date</Label>
                <div className="flex items-center gap-3">
                  <Popover open={showDatePicker} onOpenChange={setShowDatePicker}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={formData.reached_out_info.not_decided_date}
                        className={`w-[200px] justify-start text-left font-normal ${
                          !formData.reached_out_info.date && !formData.reached_out_info.not_decided_date
                            ? 'text-muted-foreground'
                            : ''
                        }`}
                        data-testid="date-picker-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.reached_out_info.date
                          ? format(new Date(formData.reached_out_info.date + 'T12:00:00'), 'PPP')
                          : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-[60]">
                      <Calendar
                        mode="single"
                        selected={
                          formData.reached_out_info.date
                            ? new Date(formData.reached_out_info.date + 'T12:00:00')
                            : undefined
                        }
                        onSelect={(date) => {
                          updateReachedOutInfo(
                            'date',
                            date
                              ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                              : ''
                          );
                          setShowDatePicker(false);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`not-decided-${formKey}`}
                      checked={formData.reached_out_info.not_decided_date}
                      onCheckedChange={(checked) => {
                        updateReachedOutInfo('not_decided_date', checked);
                        if (checked) updateReachedOutInfo('date', '');
                      }}
                      data-testid="not-decided-checkbox"
                    />
                    <Label htmlFor={`not-decided-${formKey}`} className="text-sm text-slate-500 cursor-pointer">
                      Not Decided Yet
                    </Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-600">Means of Reaching Out</Label>
                <RadioGroup
                  value={formData.reached_out_info.means}
                  onValueChange={(v) => updateReachedOutInfo('means', v)}
                  className="flex flex-wrap gap-4"
                >
                  {[
                    { value: 'message', icon: MessageSquare, label: 'Message' },
                    { value: 'call', icon: Phone, label: 'Call' },
                    { value: 'email', icon: Mail, label: 'Email' },
                    { value: 'linkedin', icon: Linkedin, label: 'LinkedIn' },
                  ].map(({ value, icon: Icon, label }) => (
                    <div key={value} className="flex items-center gap-1.5">
                      <RadioGroupItem value={value} id={`means-${value}-${formKey}`} />
                      <Label
                        htmlFor={`means-${value}-${formKey}`}
                        className="flex items-center gap-1 cursor-pointer text-sm"
                      >
                        <Icon className="w-3.5 h-3.5" /> {label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-slate-700 font-medium">Remarks</Label>
          <Textarea
            value={formData.remarks}
            onChange={(e) => updateField('remarks', e.target.value)}
            placeholder="Any notes..."
            rows={3}
            data-testid="catchup-remarks-input"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-slate-700 font-medium">Custom Tags</Label>
          <Input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }}
            placeholder="Type and press Enter to add tag"
            data-testid="catchup-tag-input"
          />
          {formData.custom_tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.custom_tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-[#0F172A]/5 text-[#0F172A] text-xs rounded-full"
                >
                  {tag}
                  <button onClick={() => handleRemoveTag(idx)} className="hover:text-red-500">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onCancel} data-testid="catchup-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white"
            data-testid="catchup-save-button"
          >
            <Save className="w-4 h-4" />
            {isEditing ? 'Update Entry' : 'Save Entry'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/* ────────────────────────────────────────────
   SubSection – one category block
   Owns its own search state.
   ──────────────────────────────────────────── */
function SubSection({
  title,
  category,
  entries,
  showForm,
  editingEntryId,
  initialFormData,
  onAddNew,
  onSave,
  onCancel,
  onEditEntry,
  onDeleteEntry,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const showInactiveWarning = category === 'maintenance';

  const filtered = useMemo(() => {
    if (!searchTerm) return entries;
    const s = searchTerm.toLowerCase();
    return entries.filter(
      (e) =>
        (e.name || '').toLowerCase().includes(s) ||
        (e.current_employer || '').toLowerCase().includes(s) ||
        (e.city || '').toLowerCase().includes(s)
    );
  }, [entries, searchTerm]);

  const inactiveCount = useMemo(() => {
    if (!showInactiveWarning) return 0;
    return entries.filter((e) => {
      const days = getInactiveDays(e);
      return days !== null && days >= 90;
    }).length;
  }, [entries, showInactiveWarning]);

  return (
    <section className="space-y-4" data-testid={`section-${category}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-xl sm:text-2xl font-heading font-semibold text-[#0F172A]">{title}</h2>
          {inactiveCount > 0 && (
            <span
              className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2.5 py-1 rounded-full"
              data-testid="inactive-count-badge"
            >
              <AlertTriangle className="w-3 h-3" />
              {inactiveCount} inactive 90d+
            </span>
          )}
        </div>
        <Button
          onClick={() => onAddNew(category)}
          className="gap-2 bg-[#D4AF37] hover:bg-[#B08D28] text-white self-start"
          data-testid={`add-catchup-${category}`}
        >
          <Plus className="w-4 h-4" />
          Add New Catch-Up
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, employer, or city..."
          className="pl-10 h-10"
          data-testid={`search-catchup-${category}`}
        />
      </div>

      {showForm === category && (
        <EntryForm
          key={editingEntryId || 'new-' + category}
          initialData={initialFormData}
          isEditing={!!editingEntryId}
          formKey={category}
          onSave={onSave}
          onCancel={onCancel}
        />
      )}

      {filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((entry) => (
            <EntryCard key={entry.id} entry={entry} onEdit={onEditEntry} onDelete={onDeleteEntry} showInactiveWarning={showInactiveWarning} />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg">
          {searchTerm ? 'No matching entries found' : 'No entries yet. Click "Add New Catch-Up" to start.'}
        </div>
      )}
    </section>
  );
}

/* ────────────────────────────────────────────
   CatchUp – main page component
   ──────────────────────────────────────────── */
export default function CatchUp() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null);
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [initialFormData, setInitialFormData] = useState({ ...EMPTY_FORM });
  const [deleteId, setDeleteId] = useState(null);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  const headers = useCallback(() => ({ Authorization: `Bearer ${tokenRef.current}` }), []);

  const sortEntries = (data) =>
    (data || []).sort((a, b) => {
      const sA = (a.name || '').split(' ').pop().toLowerCase();
      const sB = (b.name || '').split(' ').pop().toLowerCase();
      return sA.localeCompare(sB);
    });

  const refreshEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/catch-up`, { headers: headers() });
      setEntries(sortEntries(res.data));
    } catch {
      toast.error('Failed to load catch-ups');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    refreshEntries();
  }, [refreshEntries]);

  const establishmentEntries = useMemo(
    () => entries.filter((e) => (e.category || 'establishment') === 'establishment'),
    [entries]
  );
  const maintenanceEntries = useMemo(
    () => entries.filter((e) => (e.category || 'establishment') === 'maintenance'),
    [entries]
  );

  const openAddForm = useCallback((category) => {
    setInitialFormData({ ...EMPTY_FORM, category });
    setEditingEntryId(null);
    setShowForm(category);
  }, []);

  const openEditForm = useCallback((entry) => {
    setInitialFormData({
      name: entry.name || '',
      current_employer: entry.current_employer || '',
      job_title: entry.job_title || '',
      designation: entry.designation || '',
      city: entry.city || '',
      have_met_before: entry.have_met_before || false,
      category: entry.category || 'establishment',
      reached_out: entry.reached_out || false,
      reached_out_info: entry.reached_out_info || { date: '', not_decided_date: false, means: '' },
      remarks: entry.remarks || '',
      custom_tags: entry.custom_tags || [],
    });
    setEditingEntryId(entry.id);
    setShowForm(entry.category || 'establishment');
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(null);
    setEditingEntryId(null);
    setInitialFormData({ ...EMPTY_FORM });
  }, []);

  /* onSave receives final formData from EntryForm — parent state doesn't change during typing */
  const handleSave = useCallback(
    async (formData) => {
      try {
        const h = { headers: headers() };
        if (editingEntryId) {
          await axios.put(`${API_URL}/api/catch-up/${editingEntryId}`, formData, h);
          toast.success('Entry updated');
        } else {
          await axios.post(`${API_URL}/api/catch-up`, formData, h);
          toast.success('Entry added');
        }
        setShowForm(null);
        setEditingEntryId(null);
        setInitialFormData({ ...EMPTY_FORM });
        refreshEntries();
      } catch {
        toast.error('Failed to save entry');
      }
    },
    [editingEntryId, headers, refreshEntries]
  );

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/catch-up/${deleteId}`, { headers: headers() });
      toast.success('Entry deleted');
      setDeleteId(null);
      refreshEntries();
    } catch {
      toast.error('Failed to delete entry');
    }
  }, [deleteId, headers, refreshEntries]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F172A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0F172A] text-white py-4 sm:py-6 px-4 sm:px-8">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-300 hover:text-white hover:bg-slate-800 gap-2"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Contacts</span>
            <span className="sm:hidden">Back</span>
          </Button>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold">Catch-Up</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto py-6 sm:py-10 px-4 sm:px-8 space-y-12">
        <SubSection
          title="Relationship Establishment or Deepening"
          category="establishment"
          entries={establishmentEntries}
          showForm={showForm}
          editingEntryId={editingEntryId}
          initialFormData={initialFormData}
          onAddNew={openAddForm}
          onSave={handleSave}
          onCancel={closeForm}
          onEditEntry={openEditForm}
          onDeleteEntry={setDeleteId}
        />

        <div className="border-t-2 border-[#D4AF37]/30" />

        <SubSection
          title="Relationship Maintenance"
          category="maintenance"
          entries={maintenanceEntries}
          showForm={showForm}
          editingEntryId={editingEntryId}
          initialFormData={initialFormData}
          onAddNew={openAddForm}
          onSave={handleSave}
          onCancel={closeForm}
          onEditEntry={openEditForm}
          onDeleteEntry={setDeleteId}
        />
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this catch-up entry? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
